import { motion } from 'framer-motion'
import {
  FTUXElementType,
  TextElement,
  ImageElement,
  ChevronBoxElement,
  RadioBoxElement
} from '../../../types/fd-ftux.types'
import RenderHTML from '../../ui-components/RenderHTML'
import { ChevronBox } from './ChevronBox'
import { RadioBox } from './RadioBox'
import Button from '../../ui-components/Button'

interface FTUXContentProps {
  content: FTUXElementType[]
  selectedRadioBox: string | null
  handleRadioBoxClick: (radioBoxTitle: string | null) => void
  direction: 1 | -1
  isPreRender?: boolean
}

const TextElementComponent = ({
  element,
  delay,
  direction
}: {
  element: TextElement
  delay: number
  direction: 1 | -1
}) => (
  <motion.div
    initial={{ opacity: 0, x: direction * 40 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    className={`text-center ${element?.className ?? ''}`}
    style={{ maxWidth: element?.maxWidth ?? 'none', ...element?.inlineStyles }}
  >
    <RenderHTML innerHTMLText={element?.text ?? ''} />
  </motion.div>
)

const ImageElementComponent = ({
  element,
  delay,
  direction
}: {
  element: ImageElement
  delay: number
  direction: 1 | -1
}) => (
  <motion.img
    src={element.imgSrc}
    alt='FTUX Element'
    initial={{ opacity: 0, x: direction * 40 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    className={`w-full ${element?.className ?? ''}`}
    style={{ maxWidth: element?.maxWidth, ...element?.inlineStyles }}
  />
)

const ChevronBoxElementComponent = ({
  element,
  delay,
  direction
}: {
  element: ChevronBoxElement
  delay: number
  direction: 1 | -1
}) => (
  <motion.div
    initial={{ opacity: 0, x: direction * 40 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    className={`${element?.className ?? ''}`}
    style={{ maxWidth: element?.maxWidth ?? 'none', ...element?.inlineStyles }}
  >
    <ChevronBox
      iconUrl={element?.imgSrc}
      text={element?.text}
      isActionable={element?.isActionable}
      title={element?.title}
    />
  </motion.div>
)

const RadioBoxElementComponent = ({
  element,
  delay,
  direction,
  selectedRadioBox,
  handleRadioBoxClick
}: {
  element: RadioBoxElement
  delay: number
  direction: 1 | -1
  selectedRadioBox: string | null
  handleRadioBoxClick: (radioBoxTitle: string | null) => void
}) => (
  <motion.div
    initial={{ opacity: 0, x: direction * 40 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    className={`${element?.className ?? ''}`}
    style={{ maxWidth: element?.maxWidth ?? 'none', ...element?.inlineStyles }}
  >
    <Button
      onClick={() => {
        handleRadioBoxClick(element?.title ?? null)
      }}
      buttonClassName='h-max !w-full !min-w-full !max-w-full bg-transparent'
    >
      <RadioBox isSelected={selectedRadioBox === element?.title} text={element?.text} title={element?.title} />
    </Button>
  </motion.div>
)

export const FTUXContent = ({
  content,
  direction,
  selectedRadioBox,
  handleRadioBoxClick,
  isPreRender = false
}: FTUXContentProps) => {
  const renderFTUXElement = (element: FTUXElementType, index: number) => {
    const delay = isPreRender ? 0 : 0.1 + index * 0.05
    switch (element.type) {
      case 'TEXT':
        return <TextElementComponent key={index} element={element} delay={delay} direction={direction} />
      case 'IMAGE':
        return <ImageElementComponent key={index} element={element} delay={delay} direction={direction} />
      case 'CHEVRON_BOX':
        return <ChevronBoxElementComponent key={index} element={element} delay={delay} direction={direction} />
      case 'RADIO_BOX':
        return (
          <RadioBoxElementComponent
            key={index}
            element={element}
            delay={delay}
            direction={direction}
            selectedRadioBox={selectedRadioBox}
            handleRadioBoxClick={handleRadioBoxClick}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='w-full flex flex-col items-center'>
      {content.map((element, index) => renderFTUXElement(element, index))}
    </div>
  )
}
